import React, { useEffect } from 'react';
import './About.css';

const About = () => {
  useEffect(() => {
    // Check if there's a hash in the URL
    const hash = window.location.hash;
    if (hash) {
      // Remove the # from the hash
      const sectionId = hash.substring(1);
      const element = document.getElementById(sectionId);
      
      if (element) {
        // Wait a bit for the page to load, then scroll and highlight
        setTimeout(() => {
          // Get navbar height to offset scroll
          const navbar = document.querySelector('.navbar');
          const navbarHeight = navbar ? navbar.offsetHeight : 80;
          
          // Calculate scroll position with offset
          const elementTop = element.offsetTop - navbarHeight - 20;
          window.scrollTo({
            top: elementTop,
            behavior: 'smooth'
          });
          
          element.classList.add('highlight-section');
          setTimeout(() => {
            element.classList.remove('highlight-section');
          }, 3000);
        }, 500);
      }
    }
  }, []);

  return (
    <div className="about">
      <div className="container">
        <section className="about-hero">
          <h1 className="section-title">About Me</h1>
          <div className="about-content">
            <div className="about-text">
              <p className="about-intro">
                I'm Jacob T. Weinrich, a passionate software developer based just outside St. Louis, Missouri. 
                I specialize in creating innovative software solutions using modern technologies and best practices, with expertise in MERN stack and ASP.NET Core MVC development.
              </p>
              
              <p className="about-intro">
                My recent experience at Distribution Management provided me with hands-on exposure to .NET technologies, 
                including ASP.NET, Windows Forms, and .NET MAUI. I've continued growing my skills through personal 
                and freelance projects, and I'm confident in my ability to quickly learn new tools, adapt to complex 
                systems, and contribute value to development efforts.
              </p>
              
              <div className="about-details">
                <div className="detail-item">
                  <h3>Background</h3>
                  <p>
                    I have a strong foundation in both front-end and back-end development, with expertise 
                    in MERN stack, C#, ASP.NET Core MVC, and .NET MAUI. My experience spans from building responsive 
                    web applications to developing cross-platform mobile solutions.
                  </p>
                </div>
                
                <div className="detail-item">
                  <h3>Development Approach</h3>
                  <p>
                    I believe in writing clean, maintainable code and following industry best practices. 
                    My development process includes thorough testing, documentation, and collaboration with 
                    team members through code reviews and pair programming.
                  </p>
                </div>
                
                <div className="detail-item">
                  <h3>Professional Focus</h3>
                  <p>
                    I'm particularly interested in full-stack development, database management, and creating 
                    scalable applications that solve real-world problems. I enjoy working with modern 
                    frameworks and tools that enhance developer productivity and user experience.
                  </p>
                </div>
                
                <div className="detail-item">
                  <h3>Growth Mindset</h3>
                  <p>
                    While I may not meet every qualification listed in job descriptions, I'm very interested 
                    in opportunities to work with professional teams and expand my skills. I truly believe 
                    I can be a valuable asset to any development team through my adaptability, quick learning 
                    abilities, and commitment to contributing meaningful value to development efforts.
                  </p>
                </div>
                
                <div className="detail-item" id="unique-background">
                  <h3>Unique Background & User-Centric Approach</h3>
                  <p>
                    Growing up working in my family's laundromat business has given me unique insights that 
                    most developers don't have. Serving customers of all ages and technical abilities taught 
                    me to think from the user's perspective - especially those with limited technical skills. 
                    This experience drives my development philosophy: creating applications that are intuitive 
                    and accessible to everyone while still achieving their core objectives.
                  </p>
                  <p>
                    Additionally, working alongside my father's IT consulting business has provided me with 
                    extensive experience in technical troubleshooting, both over the phone and hands-on, 
                    covering software and hardware issues. This background has honed my ability to understand 
                    user pain points and develop solutions that truly serve their needs.
                  </p>
                </div>
                
                <div className="detail-item" id="ai-integration">
                  <h3>Responsible AI Integration</h3>
                  <p>
                    I believe in leveraging modern tools responsibly to enhance development efficiency. 
                    While I always prioritize independent problem-solving and debugging, I have experience 
                    using AI as a supplementary tool for code troubleshooting when appropriate. I understand 
                    the importance of data security and would only use AI tools in professional environments 
                    where they're permitted, ensuring sensitive code and information remain protected.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="about-sidebar">
                              <div className="profile-card">
                  <div className="profile-image">
                    <img src="/images/Profile-Circle-Resized.png" alt="Jacob T. Weinrich" />
                  </div>
                  <h3>Jacob T. Weinrich</h3>
                  <p className="title">Software Developer</p>
                  <div className="profile-info">
                    <div className="info-item">
                      <strong>Location:</strong>
                      <span>Just outside St. Louis, Missouri</span>
                    </div>
                    <div className="info-item">
                      <strong>Time Zone:</strong>
                      <span>Central Standard Time</span>
                    </div>
                    <div className="info-item">
                      <strong>Specialties:</strong>
                      <span>Full-Stack Development</span>
                    </div>
                    <div className="info-item">
                      <strong>Focus:</strong>
                      <span>MERN Stack, C#, ASP.NET Core MVC</span>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </section>

        <section className="about-values">
          <h2>What I Value</h2>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">💡</div>
              <h3>Innovation</h3>
              <p>
                I'm always exploring new technologies and approaches to create better solutions 
                and improve user experiences. This includes responsibly leveraging modern tools 
                like AI when appropriate and permitted.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-icon">🤝</div>
              <h3>Collaboration</h3>
              <p>
                I believe in the power of teamwork and enjoy working with others to solve 
                complex problems and share knowledge.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-icon">📚</div>
              <h3>Continuous Learning</h3>
              <p>
                The tech industry evolves rapidly, and I'm committed to staying current with 
                the latest trends and technologies. I embrace opportunities to expand my skills 
                and adapt to new tools and complex systems.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-icon">🎯</div>
              <h3>Quality</h3>
              <p>
                I focus on writing clean, efficient code and creating applications that are 
                reliable, scalable, and maintainable. My background in customer service 
                drives me to build user-friendly solutions that work for everyone.
              </p>
            </div>
          </div>
        </section>

        <section className="about-interests">
          <h2>Beyond Coding</h2>
          <div className="interests-content">
            <p>
              When I'm not coding, I enjoy staying active and exploring new technologies. 
              I'm passionate about creating software that makes a difference and helps people 
              accomplish their goals more efficiently.
            </p>
            
            <div className="interests-grid">
                          <div className="interest-item">
              <h4>User Experience</h4>
              <p>Creating intuitive interfaces and accessible solutions for users of all technical abilities</p>
            </div>
              
              <div className="interest-item">
                <h4>Problem Solving</h4>
                <p>Enjoying the challenge of breaking down complex problems into manageable solutions</p>
              </div>
              
              <div className="interest-item">
                <h4>Community</h4>
                <p>Contributing to the developer community and sharing knowledge with others</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About; 